import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { useAuthStore } from '../../stores/authStore';
import { setAuthTokenProvider } from '../../services/api/ApiService';
import { isAuditRole } from '../../utils/roles';

export function AuthCallbackPage() {
  const { user, getAccessTokenSilently, isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const processAuthentication = async () => {
      if (isLoading) {
        return;
      }

      if (error) {
        console.error('Error during authentication:', error);
        navigate('/login', { replace: true });
        return;
      }

      if (isAuthenticated && user) {
        try {
          const accessTokenResponse = await getAccessTokenSilently({
            detailedResponse: true,
          });

          // 1. Synchroniser le token Auth0 avec le backend
          await authService.refreshTokenFromAuth0(
            user,
            accessTokenResponse.access_token,
            accessTokenResponse.id_token,
            undefined, // refresh_token is handled by the SDK
            accessTokenResponse.expires_in
          );

          // 2. Configurer le provider de token pour les appels API internes
          setAuthTokenProvider(async () => {
            try {
              return await getAccessTokenSilently();
            } catch {
              return null;
            }
          });

          // 3. Charger le contexte utilisateur via /auth/me
          await useAuthStore.getState().initialize();

          // 4. Rediriger selon le rôle et le contexte organisationnel
          const { organization, user: authUser } = useAuthStore.getState();

          // Les auditeurs n'ont pas d'organisation propre → page clients
          if (isAuditRole(authUser?.role)) {
            navigate('/auditor/clients', { replace: true });
            return;
          }

          // Utilisateurs standards : vérifier l'organisation
          const hasOrganization = organization?.id || authUser?.organizationId;
          if (hasOrganization) {
            navigate('/', { replace: true });
          } else {
            // Compte authentifié mais sans organisation liée
            navigate('/no-organization', { replace: true });
          }
        } catch (tokenError) {
          console.error('Failed to process tokens with backend:', tokenError);
          navigate('/login', { replace: true });
        }
      }
    };

    processAuthentication();
  }, [isLoading, isAuthenticated, user, error, getAccessTokenSilently, navigate]);

  return <div>Loading session...</div>;
}
